import { defineQuery } from '@rocicorp/zero';
import * as z from 'zod';
import { CatalogType, DoseCalcModeActive } from '../database/schema.js';
import { EventLogResources } from '../mutators/util.js';
import { zql } from '../schema.js';
import { applyCursor } from '../util/apply-cursor.js';
import { Status } from '../util/schemas.js';
import { applyPopulate } from './populates.js';
import { Cursor, Sort } from './util.js';
/** Categories filter value matching drug catalog items that have no category assigned. */
export const NO_CATEGORIES_FILTER = 'none';
const ListDrugsFiltersInput = z.object({
    search: z.string().optional(),
    status: z.array(Status).optional(),
    categories: z.array(z.uuidv7().or(z.literal(NO_CATEGORIES_FILTER))).optional(),
});
export const ListDrugCatalogItemsInput = z.object({
    filters: ListDrugsFiltersInput.optional(),
    limit: z.number().optional(),
    cursor: Cursor.extend({
        createdAt: z.number(),
        name: z.string(),
    }).optional(),
    sort: Sort.extend({
        field: z.string().default('createdAt'),
    }),
});
export const ListActiveSubstancesFiltersInput = ListDrugsFiltersInput.extend({
    calculationMethod: z.array(DoseCalcModeActive).optional(),
    unit: z.array(z.uuidv7()).optional(),
});
export const ListActiveSubstancesInput = z.object({
    filters: ListActiveSubstancesFiltersInput.optional(),
    limit: z.number().optional(),
    cursor: Cursor.extend({
        createdAt: z.number(),
        name: z.string(),
    }).optional(),
    sort: Sort.extend({
        field: z.string().default('createdAt'),
    }),
    populate: z.enum(['minimal', 'list', 'full']).default('list'),
});
export const SolutionType = z.enum(['CARRIER_SOLUTION', 'SOLVENT']);
export const ListSolutionsFiltersInput = ListDrugsFiltersInput.extend({
    types: z.array(z.uuidv7().or(SolutionType)).optional(),
});
export const ListSolutionsInput = z.object({
    filters: ListSolutionsFiltersInput.optional(),
    limit: z.number().optional(),
    cursor: Cursor.extend({
        createdAt: z.number(),
        name: z.string(),
    }).optional(),
    sort: Sort.extend({
        field: z.string().default('createdAt'),
    }),
    populate: z.enum(['minimal', 'list', 'full']).default('list'),
});
export const ListSupportiveMedicationsFiltersInput = ListDrugsFiltersInput.extend({
    calculationMethod: z.array(DoseCalcModeActive).optional(),
    unit: z.array(z.uuidv7()).optional(),
});
export const ListSupportiveMedicationsInput = z.object({
    filters: ListSupportiveMedicationsFiltersInput.optional(),
    limit: z.number().optional(),
    cursor: Cursor.extend({
        createdAt: z.number(),
        name: z.string(),
    }).optional(),
    sort: Sort.extend({
        field: z.string().default('createdAt'),
    }),
    populate: z.enum(['minimal', 'list', 'full']).default('list'),
});
function createDrugsInitialQuery(args) {
    let q = zql.drugCatalogItems.where('deletedAt', 'IS', null);
    if (args.catalogType) {
        q = q.where('catalogType', args.catalogType);
    }
    const { status, categories } = args;
    const search = args.search?.trim();
    if (search) {
        q = q.where('name', 'ILIKE', `%${search}%`);
    }
    if (status?.length) {
        q = q.where('status', 'IN', status);
    }
    if (categories?.length) {
        const includeWithoutCategories = categories.includes(NO_CATEGORIES_FILTER);
        const categoryIds = categories.filter(id => id !== NO_CATEGORIES_FILTER);
        q = q.where(({ or, cmp, exists }) => or(includeWithoutCategories ? cmp('hasCategories', false) : undefined, categoryIds.length
            ? exists('categories', drugCategory => drugCategory.whereExists('drugCategory', category => category.where('id', 'IN', categoryIds)))
            : undefined));
    }
    return q;
}
function getDrugActivitiesById(id) {
    return zql.eventLogs
        .related('actor')
        .where('resource', '=', EventLogResources.drugCatalogItem)
        .where('resourceId', '=', id)
        .orderBy('performedAt', 'desc');
}
function getTradeNames(query) {
    return query.related('strengthUnit').related('concentrationUnit').where('deletedAt', 'IS', null);
}
function getDrugMedicationProfiles(query) {
    return query
        .related('medicineForm')
        .related('stabilityRules', rules => rules
        .related('stabilityRule', rule => rule
        .related('storageLabel')
        .related('containerMaterials', materials => materials.where('deletedAt', 'IS', null)))
        .where('deletedAt', 'IS', null))
        .related('tradeNames', getTradeNames)
        .related('allowedReconstitutionDiluents', reconstitutionDiluents => reconstitutionDiluents.related('solutionDrug').where('deletedAt', 'IS', null))
        .where('deletedAt', 'IS', null);
}
// #region POPULATE PROFILES
/**
 * Populate profiles for active substance queries.
 *
 * - `minimal`: No relations. Use for dropdowns that only need `id` + `name`.
 * - `list`: activeSubstanceDetails (with doseUnit, hazardClasses), categories, created.
 * - `full`: Everything including medicationProfiles (4-level depth).
 */
export const activeSubstancePopulates = {
    minimal: null,
    list: q => q
        .related('activeSubstanceDetails', substance => substance.related('doseUnit').related('hazardClasses', j => j.related('hazardClass')))
        .related('categories', drugCategory => drugCategory.related('drugCategory'))
        .related('created'),
    full: q => q
        .related('activeSubstanceDetails', substance => substance.related('doseUnit').related('hazardClasses', j => j.related('hazardClass')))
        .related('supportiveMedicationDetails', substance => substance.related('doseUnit'))
        .related('medicationProfiles', profile => getDrugMedicationProfiles(profile))
        .related('categories', drugCategory => drugCategory.related('drugCategory'))
        .related('created'),
};
/**
 * Populate profiles for solution queries.
 *
 * - `minimal`: No relations.
 * - `list`: solutionDetails (with concentrationUnit, types), categories, created.
 * - `full`: Everything including medicationProfiles.
 */
export const solutionPopulates = {
    minimal: null,
    list: q => q
        .related('solutionDetails', solution => 
    // a removed type stays as a soft-deleted row; adding it back inserts a new one (CY-237)
    solution
        .related('concentrationUnit')
        .related('types', type => type.related('solutionType').where('deletedAt', 'IS', null)))
        .related('categories', drugCategory => drugCategory.related('drugCategory'))
        .related('created'),
    full: q => q
        .related('solutionDetails', solution => solution
        .related('concentrationUnit')
        .related('types', type => type.related('solutionType').where('deletedAt', 'IS', null)))
        .related('medicationProfiles', profile => getDrugMedicationProfiles(profile))
        .related('categories', drugCategory => drugCategory.related('drugCategory').where('deletedAt', 'IS', null))
        .related('created'),
};
/**
 * Populate profiles for supportive medication queries.
 *
 * - `minimal`: No relations.
 * - `list`: supportiveMedicationDetails (with doseUnit), categories, created.
 * - `full`: Everything including medicationProfiles.
 */
export const supportiveMedicationPopulates = {
    minimal: null,
    list: q => q
        .related('supportiveMedicationDetails', details => details.related('doseUnit'))
        .related('categories', drugCategory => drugCategory.related('drugCategory'))
        .related('created'),
    full: q => q
        .related('supportiveMedicationDetails', details => details.related('doseUnit'))
        .related('medicationProfiles', profile => getDrugMedicationProfiles(profile))
        .related('categories', drugCategory => drugCategory.related('drugCategory').where('deletedAt', 'IS', null))
        .related('created'),
};
// #endregion
export const drugCatalogQueries = {
    listActiveSubstances: defineQuery(ListActiveSubstancesInput, ({ args }) => {
        let q = createDrugsInitialQuery({ ...args.filters, catalogType: CatalogType.enum.ACTIVE_SUBSTANCE });
        if (args.filters) {
            const { calculationMethod, unit } = args.filters;
            if (calculationMethod?.length) {
                q = q.whereExists('activeSubstanceDetails', substance => substance.where('doseCalcMode', 'IN', calculationMethod));
            }
            if (unit?.length) {
                q = q.whereExists('activeSubstanceDetails', substance => substance.where('doseUnitId', 'IN', unit));
            }
        }
        q = applyCursor(q, { cursor: args.cursor, sort: args.sort, limit: args.limit });
        q = applyPopulate(q, activeSubstancePopulates, args.populate);
        return q;
    }),
    listDrugCatalogItems: defineQuery(ListDrugCatalogItemsInput, ({ args }) => {
        let q = createDrugsInitialQuery({ ...args.filters }).where('catalogType', 'IN', [
            CatalogType.enum.ACTIVE_SUBSTANCE,
            CatalogType.enum.SUPPORTIVE_MEDICATION,
        ]);
        q = q
            .related('activeSubstanceDetails', substance => substance.related('doseUnit'))
            .related('supportiveMedicationDetails', details => details.related('doseUnit'));
        q = applyCursor(q, { cursor: args.cursor, sort: args.sort, limit: args.limit });
        return q;
    }),
    getDrugCatalogItem: defineQuery(z.string(), ({ args }) => {
        return zql.drugCatalogItems
            .related('activeSubstanceDetails', substance => substance.related('doseUnit').related('hazardClasses', j => j.related('hazardClass')))
            .related('supportiveMedicationDetails', details => details.related('doseUnit'))
            .related('medicationProfiles', profile => getDrugMedicationProfiles(profile))
            .related('categories', drugCategory => drugCategory.related('drugCategory'))
            .related('created')
            .where('id', '=', args)
            .where('deletedAt', 'IS', null)
            .one();
    }),
    getActiveSubstanceById: defineQuery(z.string(), ({ args }) => {
        return zql.drugCatalogItems
            .related('activeSubstanceDetails', substance => substance.related('doseUnit').related('hazardClasses', j => j.related('hazardClass')))
            .related('medicationProfiles', profile => getDrugMedicationProfiles(profile))
            .related('categories', drugCategory => drugCategory.related('drugCategory'))
            .related('created')
            .where('id', '=', args)
            .where('deletedAt', 'IS', null)
            .one();
    }),
    getActiveSubstanceActivitiesById: defineQuery(z.string(), ({ args }) => getDrugActivitiesById(args)),
    getAllActiveSubstances: defineQuery(_params => {
        let q = zql.drugCatalogItems
            .where('catalogType', 'IN', [CatalogType.enum.ACTIVE_SUBSTANCE, CatalogType.enum.SUPPORTIVE_MEDICATION])
            .where('deletedAt', 'IS', null);
        q = applyPopulate(q, activeSubstancePopulates, 'minimal');
        q = q.orderBy('name', 'asc');
        return q;
    }),
    listSolutions: defineQuery(ListSolutionsInput, ({ args }) => {
        let q = createDrugsInitialQuery({ ...args.filters, catalogType: CatalogType.enum.SOLUTION });
        if (args.filters) {
            const { types } = args.filters;
            if (types?.length) {
                q = q.whereExists('solutionDetails', solution => solution.whereExists('types', type => type
                    .where('deletedAt', 'IS', null)
                    .whereExists('solutionType', solutionType => solutionType.where(({ or, cmp }) => or(cmp('id', 'IN', types), cmp('code', 'IN', types))))));
            }
        }
        q = applyCursor(q, { cursor: args.cursor, sort: args.sort, limit: args.limit });
        q = applyPopulate(q, solutionPopulates, args.populate);
        return q;
    }),
    getSolutionById: defineQuery(z.string(), ({ args }) => {
        return zql.drugCatalogItems
            .related('solutionDetails', solution => solution
            .related('concentrationUnit')
            .related('types', type => type.related('solutionType').where('deletedAt', 'IS', null)))
            .related('medicationProfiles', profile => getDrugMedicationProfiles(profile))
            .related('categories', drugCategory => drugCategory.related('drugCategory').where('deletedAt', 'IS', null))
            .related('created')
            .where('id', '=', args)
            .where('deletedAt', 'IS', null)
            .one();
    }),
    getSolutionActivitiesById: defineQuery(z.string(), ({ args }) => getDrugActivitiesById(args)),
    listSupportiveMedications: defineQuery(ListSupportiveMedicationsInput, ({ args }) => {
        let q = createDrugsInitialQuery({ ...args.filters, catalogType: CatalogType.enum.SUPPORTIVE_MEDICATION });
        if (args.filters) {
            const { calculationMethod, unit } = args.filters;
            if (calculationMethod?.length) {
                q = q.whereExists('supportiveMedicationDetails', substance => substance.where('doseCalcMode', 'IN', calculationMethod));
            }
            if (unit?.length) {
                q = q.whereExists('supportiveMedicationDetails', substance => substance.where('doseUnitId', 'IN', unit));
            }
        }
        q = applyCursor(q, { cursor: args.cursor, sort: args.sort, limit: args.limit });
        q = applyPopulate(q, supportiveMedicationPopulates, args.populate);
        return q;
    }),
    getSupportiveMedicationById: defineQuery(z.string(), ({ args }) => {
        return zql.drugCatalogItems
            .related('supportiveMedicationDetails', details => details.related('doseUnit'))
            .related('medicationProfiles', profile => getDrugMedicationProfiles(profile))
            .related('categories', drugCategory => drugCategory.related('drugCategory').where('deletedAt', 'IS', null))
            .related('created')
            .where('id', '=', args)
            .where('deletedAt', 'IS', null)
            .one();
    }),
    getSupportiveMedicationActivitiesById: defineQuery(z.string(), ({ args }) => getDrugActivitiesById(args)),
};
//# sourceMappingURL=drug-catalog.queries.js.map